/**
 * PlatformDescriptionPage (iter1068, преведена iter1070) —
 * „Описание на платформата". Единствен подробен текст за това какво е
 * Nutramilo, как е устроена мрежата от близнаци и какво влиза в оценките.
 * Имената и броят на близнаците се четат от единния каталог.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import SEO from "@/components/SEO";
import SubpageHero from "@/components/SubpageHero";
import { LanguageSwitcher, SubpageFooter } from "@/components/SubpageLayout";
import {
  Network, Map, Utensils, Mic, CalendarCheck, GitBranch, ShieldCheck,
  FlaskConical, ArrowRight, AlertTriangle, UserRound, ArrowLeft, Home,
} from "lucide-react";
import { TWIN_CATALOG, TWIN_IDS } from "@/lib/twinCatalog";
import { DR_INKOV_PHOTO } from "@/data/consultants";
import { twinLabel } from "@/components/landing/twinNetworkCopy";
import { pickTx } from "./platformDescriptionCopy";

const ICONS = [Network, Map, FlaskConical, Mic, Utensils, CalendarCheck,
  GitBranch, ShieldCheck];

export default function PlatformDescriptionPage() {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const t = pickTx(language);
  const card = isDark
    ? "rounded-2xl border border-slate-800 bg-[#0F172A] p-6"
    : "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm";

  return (
    <div data-testid="platform-description-page"
      className={`min-h-screen ${isDark ? "bg-[#090D16] text-slate-100" : "bg-[#FAFBFC] text-slate-900"}`}>
      <SEO title={`${t.eyebrow} · Nutramilo`} description={t.seo_desc} path="/platform" />

      {/* Back-to-home navigation (iter — fix orphaned subpage) */}
      <div
        data-testid="platform-sticky-nav"
        className={`sticky top-0 z-30 backdrop-blur-xl border-b ${
          isDark ? "bg-[#090D16]/85 border-slate-800" : "bg-white/85 border-slate-100"
        }`}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="platform-nav-home"
            className={`inline-flex items-center gap-2 text-sm font-semibold ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-teal-700"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Към Nutramilo</span>
            <span className="sm:hidden">Начало</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/tools"
              data-testid="platform-nav-tools"
              className={`text-xs font-medium ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-teal-700"
              }`}
            >
              Инструменти
            </Link>
            <Link
              to="/scientific-formulas"
              data-testid="platform-nav-science"
              className={`text-xs font-medium ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-teal-700"
              }`}
            >
              Методика
            </Link>
            <Link
              to="/pricing"
              data-testid="platform-nav-pricing"
              className={`text-xs font-medium ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-teal-700"
              }`}
            >
              Цени
            </Link>
            <LanguageSwitcher isDark={isDark} testIdPrefix="platform-lang" />
            <Link
              to="/register"
              data-testid="platform-nav-register"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-4 py-2 rounded-full text-white"
              style={{
                background: "linear-gradient(135deg, #0e5c60 0%, #68c349 100%)",
              }}
            >
              Безплатно
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <SubpageHero
          eyebrow={t.eyebrow}
          title="Nutramilo"
          subtitle={`${t.lead_1} ${TWIN_IDS.length} ${t.lead_2}`}
          icon={Network}
          accent="#0e5c60"
          testId="platform-description-title"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12"
          data-testid="platform-pillars">
          {t.pillars.map((p, i) => {
            const Icon = ICONS[i] || Network;
            return (
              <div key={p.title} className={card}
                data-testid={`platform-pillar-${i}`}>
                <div className={`w-10 h-10 rounded-xl grid place-items-center mb-4 ${
                  isDark ? "bg-teal-500/10 text-teal-300" : "bg-teal-50 text-teal-700"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="text-base md:text-lg font-semibold">{p.title}</h2>
                <p className={`text-sm leading-relaxed mt-2 ${
                  isDark ? "text-slate-400" : "text-slate-600"}`}>{p.text}</p>
              </div>
            );
          })}
        </div>

        <h2 className="text-base md:text-lg font-semibold mt-16 mb-2">
          {TWIN_IDS.length}{t.twins_heading_suffix}
        </h2>
        <p className={`text-sm mb-5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          {t.twins_note}
        </p>
        <div className={`overflow-hidden ${card} p-0`} data-testid="platform-twins-table">
          <table className="w-full text-left">
            <thead className={isDark ? "bg-slate-900/60" : "bg-slate-50"}>
              <tr>
                <th className="text-xs font-bold uppercase tracking-wider px-4 py-3">{t.col_twin}</th>
                <th className="text-xs font-bold uppercase tracking-wider px-4 py-3">{t.col_feeds}</th>
              </tr>
            </thead>
            <tbody>
              {TWIN_IDS.map((id) => (
                <tr key={id} data-testid={`platform-twin-row-${id}`}
                  className={isDark ? "border-t border-slate-800" : "border-t border-slate-100"}>
                  <td className="px-4 py-3 text-sm font-semibold align-top whitespace-normal">
                    {twinLabel(id, language, TWIN_CATALOG[id].label)}
                  </td>
                  <td className={`px-4 py-3 text-xs align-top ${
                    isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {t.feeds[id]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* iter1070 — създателят на платформата */}
        <div className={`${card} mt-10`} data-testid="platform-founder">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <img src={DR_INKOV_PHOTO} alt="Dr Ivan Inkov" loading="lazy"
              className="w-20 h-20 rounded-xl object-cover shrink-0 ring-1 ring-slate-200 dark:ring-slate-800" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <UserRound className="w-4 h-4 text-teal-600 dark:text-teal-300" />
                <h2 className="text-base md:text-lg font-semibold">{t.founder_title}</h2>
              </div>
              <p className={`text-sm leading-relaxed mt-2 ${
                isDark ? "text-slate-400" : "text-slate-600"}`}>{t.founder_text}</p>
              <Link to="/consultants/inkov" data-testid="platform-founder-cta"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold mt-3 ${
                  isDark ? "text-teal-300 hover:text-teal-200" : "text-teal-700 hover:text-teal-900"}`}>
                {t.founder_cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className={`${card} mt-10 border-l-4 border-l-amber-500`}
          data-testid="platform-limits">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
            <div>
              <h2 className="text-base md:text-lg font-semibold">{t.limits_title}</h2>
              <ul className={`text-sm leading-relaxed mt-2 space-y-1.5 ${
                isDark ? "text-slate-400" : "text-slate-600"}`}>
                {t.limits.map((l) => <li key={l}>· {l}</li>)}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-10">
          <Link to="/twins/network" data-testid="platform-description-network-cta"
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white ${
              isDark ? "bg-emerald-600 hover:bg-emerald-500" : "bg-[#15803D] hover:bg-[#166534]"}`}>
            {t.cta_network} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/dashboard/marker-map" data-testid="platform-description-marker-map-cta"
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold border ${
              isDark ? "border-slate-700 text-slate-200 hover:bg-white/5"
                : "border-slate-300 text-slate-800 hover:bg-white"}`}>
            <Map className="w-4 h-4" /> {t.cta_marker_map}
          </Link>
        </div>
      </div>
      <SubpageFooter />
    </div>
  );
}
